// An offer version's terms as staff edit them (PLAN-offers.md §4). Amounts excluding VAT; usage amounts in euros
// of client price. Blank allowance = unlimited; blank user cap = no cap per member. Credit tiers (§15): the monthly
// usage levels a subscriber picks from, each a price and the usage it gives; none = no choice.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toDecimal } from '../../services/format';
import { Button, Checkbox, Field, Input, Textarea } from '../../components/ui';
import type { CreditTier, Terms } from '../../types/api';

type TierRow = { price: string; usage: string };

const tierRows = (tiers: CreditTier[] | null): TierRow[] => (tiers ?? []).map((x) => ({ price: String(x.price), usage: String(x.usage) }));

export const EMPTY_TERMS: Terms = {
  currency: 'EUR',
  price_per_seat: '0.00',
  allowance_per_seat_eur: '20.00',
  user_cap_eur: null,
  min_seats: 1,
  max_seats: null,
  trial_days: null,
  features: [],
  credit_tiers: null,
};

export function TermsForm({ initial, busy, submitLabel, onSubmit, notice }: { initial: Terms; busy: boolean; submitLabel: ReactNode; onSubmit: (terms: Terms) => void; notice?: ReactNode }) {
  const { t } = useTranslation();
  const [price, setPrice] = useState(initial.price_per_seat);
  const [allowance, setAllowance] = useState(initial.allowance_per_seat_eur ?? '');
  const [cap, setCap] = useState(initial.user_cap_eur ?? '');
  const [minSeats, setMinSeats] = useState(String(initial.min_seats));
  const [maxSeats, setMaxSeats] = useState(initial.max_seats === null ? '' : String(initial.max_seats));
  const [trial, setTrial] = useState(initial.trial_days !== null);
  const [trialDays, setTrialDays] = useState(String(initial.trial_days ?? 14));
  const [features, setFeatures] = useState(initial.features.join('\n'));
  const [tiers, setTiers] = useState<TierRow[]>(tierRows(initial.credit_tiers));
  // Other terms to show (a version saved, another offer): the fields take them. Not on mount, where the state above
  // already holds them: run then, after the first paint, it put the old price back into a field the user had just
  // cleared, and "35" typed after it was saved as 2935 (the vitest 4 run of Billing.test.tsx, 2026-10-09).
  const shown = useRef(initial);
  useEffect(() => {
    if (shown.current === initial) return;
    shown.current = initial;
    setPrice(initial.price_per_seat);
    setAllowance(initial.allowance_per_seat_eur ?? '');
    setCap(initial.user_cap_eur ?? '');
    setMinSeats(String(initial.min_seats));
    setMaxSeats(initial.max_seats === null ? '' : String(initial.max_seats));
    setTrial(initial.trial_days !== null);
    setTrialDays(String(initial.trial_days ?? 14));
    setFeatures(initial.features.join('\n'));
    setTiers(tierRows(initial.credit_tiers));
  }, [initial]);
  const setTier = (i: number, patch: Partial<TierRow>) => setTiers((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({
      currency: initial.currency || 'EUR',
      price_per_seat: trial ? '0.00' : (toDecimal(price) ?? '0.00'),
      allowance_per_seat_eur: toDecimal(allowance),
      user_cap_eur: trial ? null : toDecimal(cap),
      min_seats: Math.max(1, parseInt(minSeats, 10) || 1),
      max_seats: maxSeats.trim() ? Math.max(1, parseInt(maxSeats, 10) || 1) : null,
      trial_days: trial ? Math.max(1, parseInt(trialDays, 10) || 14) : null,
      features: features.split('\n').map((f) => f.trim()).filter(Boolean).slice(0, 12),
      credit_tiers: trial ? null : complete(tiers),
    });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      {notice}
      <Checkbox label={t('offers.isTrial')} checked={trial} onChange={(e) => setTrial(e.target.checked)} />
      <div className="grid grid-cols-2 gap-3">
        {trial ? (
          <Field label={t('offers.trialDays')}>
            <Input type="number" min={1} max={365} value={trialDays} onChange={(e) => setTrialDays(e.target.value)} data-testid="terms-trial-days" />
          </Field>
        ) : (
          <Field label={t('offers.pricePerSeat')} hint={t('offers.pricePerSeatHint')}>
            <Input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="terms-price" />
          </Field>
        )}
        <Field label={trial ? t('offers.trialAllowance') : t('offers.allowancePerSeat')} hint={t('offers.allowanceHint')}>
          <Input type="number" min={0} step="0.01" value={allowance} onChange={(e) => setAllowance(e.target.value)} placeholder={t('orgs.unlimited')} data-testid="terms-allowance" />
        </Field>
      </div>
      {!trial && (
        <Field label={t('offers.userCap')} hint={t('offers.userCapHint')}>
          <Input type="number" min={0} step="0.01" value={cap} onChange={(e) => setCap(e.target.value)} placeholder={t('offers.noCap')} data-testid="terms-cap" />
        </Field>
      )}
      {!trial && (
        <Field label={t('offers.tiers')} hint={t('offers.tiersHint')}>
          <div className="flex flex-col gap-2" data-testid="terms-tiers">
            {tiers.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
                <Input type="number" min={0} step="0.01" value={row.price} onChange={(e) => setTier(i, { price: e.target.value })} aria-label={t('offers.tierPrice')} placeholder={t('offers.tierPrice')} data-testid={`tier-price-${i}`} />
                <Input type="number" min={0} step="0.01" value={row.usage} onChange={(e) => setTier(i, { usage: e.target.value })} aria-label={t('offers.tierUsage')} placeholder={t('offers.tierUsage')} data-testid={`tier-usage-${i}`} />
                <Button variant="ghost" onClick={() => setTiers((rows) => rows.filter((_, j) => j !== i))} aria-label={t('offers.removeTier')}>
                  ×
                </Button>
              </div>
            ))}
            <div>
              <Button variant="ghost" onClick={() => setTiers((rows) => [...rows, { price: '', usage: '' }])} data-testid="tier-add">
                {t('offers.addTier')}
              </Button>
            </div>
          </div>
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('offers.minSeats')}>
          <Input type="number" min={1} value={minSeats} onChange={(e) => setMinSeats(e.target.value)} />
        </Field>
        <Field label={t('offers.maxSeats')} hint={t('offers.maxSeatsHint')}>
          <Input type="number" min={1} value={maxSeats} onChange={(e) => setMaxSeats(e.target.value)} placeholder="∞" />
        </Field>
      </div>
      <Field label={t('offers.features')} hint={t('offers.featuresHint')}>
        <Textarea value={features} onChange={(e) => setFeatures(e.target.value)} />
      </Field>
      <div className="flex justify-end">
        <Button type="submit" variant="primary" busy={busy} data-testid="terms-save">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** The filled rows, as the API takes them; none = no tiers. A row missing its usage gives its price as usage. */
function complete(rows: TierRow[]): CreditTier[] | null {
  const out = rows
    .map((r) => ({ price: toDecimal(r.price), usage: toDecimal(r.usage) ?? toDecimal(r.price) }))
    .filter((r): r is CreditTier => r.price !== null && r.usage !== null)
    .sort((a, b) => Number(a.price) - Number(b.price));
  return out.length ? out : null;
}
